import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import i18n from '@/i18n';
import logger from '@/lib/logger';

const API = '/api';
const AuthContext = createContext(null);

// Auth is cookie-based. The backend issues an httpOnly, Secure cookie
// (`auth_token` for JWT, `session_token` for Google OAuth) that the browser
// sends automatically on same-origin /api requests. No token is ever exposed
// to JavaScript, so a stolen-token-via-XSS attack is not possible.

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Axios instance that always sends the httpOnly auth cookie.
  const authAxios = useMemo(() => axios.create({ withCredentials: true }), []);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      const res = await authAxios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch (err) {
      // 401 is expected when not logged in; only log unexpected errors.
      if (err?.response?.status !== 401) {
        logger.warn('[AuthContext] checkAuth failed:', err?.message || err);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const loginJWT = async (email, password) => {
    const res = await authAxios.post(`${API}/auth/login`, { email, password });
    setUser(res.data.user);
    return res.data.user;
  };

  const loginDemo = async () => {
    const res = await authAxios.post(`${API}/auth/demo`, {});
    setUser(res.data.user);
    return res.data.user;
  };

  const registerJWT = async (email, password, name) => {
    const res = await authAxios.post(`${API}/auth/register`, { email, password, name, lang: i18n.language });
    setUser(res.data.user);
    return res.data.user;
  };

  const loginGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const pathParts = window.location.pathname.split('/').filter(Boolean);
    const lang = pathParts.length > 0 && ['en','de','es','fr','nl'].includes(pathParts[0]) ? pathParts[0] : 'en';
    const redirectUrl = window.location.origin + `/${lang}/calculator`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const exchangeSession = async (sessionId) => {
    const res = await authAxios.post(`${API}/auth/session`, { session_id: sessionId });
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = async () => {
    try {
      await authAxios.post(`${API}/auth/logout`, {});
    } catch (err) {
      // Server logout failure is non-fatal — the cookie is cleared server-side
      // and we still clear local user state below.
      logger.debug('[AuthContext] logout call failed (continuing local cleanup):', err?.message || err);
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, loginJWT, loginDemo, registerJWT, loginGoogle, exchangeSession, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
